import { Controller } from "@hotwired/stimulus"
import { formatBillingAmount } from "../../utils/billing-amount.util"
import { pluralText } from "../../utils/plural-category.util"

// Fallbacks for the server-side copy passed in through the `translations`
// value; a key the view did not send never reads as `undefined`. The sentence
// entries carry %{name} / %{count} placeholders writeCouponSentence fills in.
const DEFAULT_TRANSLATIONS = {
  discountBadgeSuffix: "discount",
  couponLabel: "Coupon",
  invalidCodeError: "Invalid promo code",
  networkError: "Network error. Please try again.",
  sentenceOnce: "",
  sentenceForever: ""
}

export default class extends Controller {
  static targets = [
    "toggle",
    "form",
    "input",
    "applyButton",
    "spinner",
    "error",
    "result",
    "promotionCodeField",
    "discountLine",
    "discountLabel",
    "discountAmount",
    "heroPrice",
    "couponSavingsLine",
    "preDiscountPrice",
    "couponPercent",
    "summaryPlanAmount",
    "summarySubtotal",
    "summaryVat",
    "summaryVatRow",
    "summaryChargeToday",
    // An upgrade's unused-time credit (below VAT) and the part of it the
    // charge does not use up, which stays on the customer balance.
    "summaryCredit",
    "summaryRemainingCredit",
    "summaryRemainingCreditRow",
    "summaryRenewal",
    "summaryRenewalRow",
    "couponSentence",
    // Echoes of the summary amounts rendered elsewhere on the page — the submit
    // button, the start-mode cards and their copy. Several per page, never one.
    "firstPaymentEcho",
    "renewalEcho",
    // The start-mode cards: plain links the server re-renders, so a coupon
    // applied live must travel in their query string or the switch drops it.
    "modeLink"
  ]
  static values = {
    translations: { type: Object, default: {} },
    validateUrl: String,
    planId: String,
    interval: String,
    // The whole repeating-sentence plural table (one/few/many/other), keyed
    // by the form the page's language can select; pluralText() picks one per
    // coupon length.
    sentenceRepeatingTexts: { type: Object, default: {} },
    // BillingHelper#currency_format_data — the page language's price format.
    currencyFormat: { type: Object, default: {} },
    renewalBaseCents: { type: Number, default: 0 },
    vatRate: { type: Number, default: 0 },
    // Name of a coupon the server already quoted (retained on the subscription,
    // or carried in the URL across a mode switch): the section opens applied.
    appliedName: { type: String, default: "" }
  }

  get #texts() {
    return { ...DEFAULT_TRANSLATIONS, ...this.translationsValue }
  }

  // A server-quoted coupon opens the section in the applied state: chip, no
  // input, and no remove control (a retained coupon cannot be removed here; a
  // URL-carried one is dropped by leaving the checkout). No `applied` event —
  // the page, the wallet sheet included, is already priced with it.
  connect() {
    if (!this.appliedNameValue || !this.hasResultTarget) return
    this.renderChip(this.appliedNameValue, { removable: false })
    this.lockInput()
    if (this.hasToggleTarget) this.toggleTarget.classList.add("d-none")
    if (this.hasFormTarget) this.formTarget.classList.add("d-none")
  }

  toggle(event) {
    event.preventDefault()
    this.formTarget.classList.toggle("d-none")
    if (!this.formTarget.classList.contains("d-none")) {
      this.inputTarget.focus()
    }
  }

  async apply() {
    if (this.applyButtonTarget.disabled) return
    const code = this.inputTarget.value.trim()
    if (!code) return

    this.setLoading(true)
    this.clearMessages()

    try {
      const response = await fetch(this.validateUrlValue, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')?.content
        },
        body: JSON.stringify({
          code: code,
          plan_id: this.planIdValue,
          interval: this.intervalValue
        })
      })

      const data = await response.json()

      if (data.valid) {
        this.applyDiscount(data)
      } else {
        console.error("[coupon] validation failed", data.error)
        this.showError(data.error || this.#texts.invalidCodeError)
      }
    } catch (err) {
      console.error("[coupon] fetch", err)
      this.showError(this.#texts.networkError)
    } finally {
      this.setLoading(false)
    }
  }

  remove(event) {
    event.preventDefault()
    this.promotionCodeFieldTargets.forEach((field) => {
      field.value = ""
    })
    this.writeModeLinks(null)
    this.resultTarget.classList.add("d-none")
    this.resultTarget.innerHTML = ""
    this.inputTarget.value = ""
    this.inputTarget.disabled = false
    this.couponApplied = false
    this.applyButtonTarget.removeAttribute("disabled")
    if (this.hasDiscountLineTarget) {
      this.discountLineTarget.classList.add("d-none")
    }
    this.restoreSummaryBreakdown()
    this.restoreRenewal()
    if (this.hasCouponSentenceTarget) this.couponSentenceTarget.textContent = ""
    this.dispatch("removed")
  }

  keydown(event) {
    if (event.key === "Enter") {
      event.preventDefault()
      this.apply()
    }
  }

  applyDiscount(data) {
    this.promotionCodeFieldTargets.forEach((field) => {
      field.value = data.promotion_code_id
    })
    this.writeModeLinks(data.promotion_code_id)
    this.renderChip(data.coupon_name, { removable: true })
    this.lockInput()

    // On an upgrade the coupon also discounts the carried add-on lines, which
    // the server folds into summary_discount_cents; the renewal (plan only)
    // keeps plan_discount_cents.
    const discountCents = data.summary_discount_cents ?? data.plan_discount_cents
    if (this.hasDiscountLineTarget && this.hasDiscountAmountTarget) {
      this.discountLineTarget.classList.remove("d-none")
      this.discountAmountTarget.textContent = `-${this.formatCents(discountCents)}`
      if (this.hasDiscountLabelTarget) {
        this.discountLabelTarget.innerHTML = `${this.escapeHtml(this.#texts.couponLabel)} <span class="billing__discount-badge">${this.escapeHtml(data.coupon_name)}</span>`
      }
    }

    this.updateSummaryBreakdown(discountCents)
    this.updateRenewal(data)
    this.writeCouponSentence(data)
    this.dispatch("applied", { detail: { amount: data.deferred_amount_cents } })
  }

  // The same chip the server renders for a coupon it already quoted; the
  // remove control only for a code applied live on this page.
  renderChip(name, { removable }) {
    const remove = removable
      ? `<a href="#" class="ms-1 d-inline-flex align-items-center text-decoration-none" data-action="click->billing--coupon#remove">
           <span class="material-symbols-outlined" style="font-size: 14px;">close</span>
         </a>`
      : ""
    this.resultTarget.classList.remove("d-none")
    this.resultTarget.innerHTML = `
      <span class="badge badge--success d-inline-flex align-items-center gap-1">
        <span class="material-symbols-outlined" style="font-size: 14px;">local_offer</span>
        ${this.escapeHtml(name)}
        ${remove}
      </span>
    `
  }

  lockInput() {
    this.couponApplied = true
    this.inputTarget.disabled = true
    this.applyButtonTarget.setAttribute("disabled", "disabled")
  }

  showError(message) {
    this.errorTarget.textContent = message
    this.errorTarget.classList.remove("d-none")
  }

  clearMessages() {
    this.errorTarget.textContent = ""
    this.errorTarget.classList.add("d-none")
    this.resultTarget.classList.add("d-none")
  }

  setLoading(loading) {
    if (loading || !this.couponApplied) {
      this.applyButtonTarget.disabled = loading
    }
    if (this.hasSpinnerTarget) {
      this.spinnerTarget.classList.toggle("d-none", !loading)
    }
  }

  // The summary renders the subtotal and VAT rows only when VAT is charged. At
  // 0 % VAT (reverse charge, non-EU) the charge-today row is the only one, and
  // its data-cents is then the net base — so the base comes from whichever of
  // the two is on the page, and VAT is 0 without a VAT row. An upgrade always
  // renders the subtotal (the net before its credit), and its credit comes off
  // after VAT: Stripe applies the customer-balance credit to the VAT-inclusive
  // total, so the credit never lowers the VAT.
  updateSummaryBreakdown(discountCents) {
    const baseEl = this.hasSummarySubtotalTarget
      ? this.summarySubtotalTarget
      : this.hasSummaryChargeTodayTarget
        ? this.summaryChargeTodayTarget
        : null
    if (!baseEl) return
    const newSubtotalCents = parseInt(baseEl.dataset.cents, 10) - discountCents
    if (this.hasSummarySubtotalTarget) this.rememberAndWrite(this.summarySubtotalTarget, this.formatCents(newSubtotalCents))

    // The VAT row is rewritten whenever it exists — a coupon that covers the
    // whole net price must show 0.00 there too, not the pre-discount amount.
    const netCents = Math.max(newSubtotalCents, 0)
    let vatCents = 0
    if (this.hasSummaryVatTarget) {
      const vatEl = this.summaryVatTarget
      const vatRate = parseFloat(vatEl.dataset.rate || "0")
      vatCents = Math.round((netCents * vatRate) / 100)
      this.rememberAndWrite(vatEl, this.formatCents(vatCents))
    }

    const grossCents = netCents + vatCents
    const creditCents = this.hasSummaryCreditTarget ? parseInt(this.summaryCreditTarget.dataset.cents, 10) : 0

    // The same charge-today text lands on every echo — the submit button, the
    // "activate now" card, the trial card's first-payment sentence.
    const chargeText = this.formatCents(Math.max(grossCents - creditCents, 0))
    if (this.hasSummaryChargeTodayTarget) this.rememberAndWrite(this.summaryChargeTodayTarget, chargeText)
    for (const el of this.firstPaymentEchoTargets) this.rememberAndWrite(el, chargeText)
    this.writeRemainingCredit(Math.max(creditCents - grossCents, 0))
  }

  writeRemainingCredit(cents) {
    if (!this.hasSummaryRemainingCreditTarget || !this.hasSummaryRemainingCreditRowTarget) return
    const row = this.summaryRemainingCreditRowTarget
    if (row.dataset.originalHidden === undefined) {
      row.dataset.originalHidden = row.classList.contains("d-none") ? "true" : "false"
    }
    this.rememberAndWrite(this.summaryRemainingCreditTarget, this.formatCents(cents))
    row.classList.toggle("d-none", cents <= 0)
  }

  restoreSummaryBreakdown() {
    for (const target of ["summarySubtotal", "summaryVat", "summaryChargeToday", "summaryRemainingCredit"]) {
      const hasTarget = `has${target.charAt(0).toUpperCase() + target.slice(1)}Target`
      if (this[hasTarget]) this.restoreOriginal(this[`${target}Target`])
    }
    for (const el of this.firstPaymentEchoTargets) this.restoreOriginal(el)
    const remainingRow = this.hasSummaryRemainingCreditRowTarget ? this.summaryRemainingCreditRowTarget : null
    if (remainingRow?.dataset.originalHidden !== undefined) {
      remainingRow.classList.toggle("d-none", remainingRow.dataset.originalHidden === "true")
    }
  }

  rememberAndWrite(el, text) {
    if (!el.dataset.originalValue) el.dataset.originalValue = el.textContent
    el.textContent = text
  }

  restoreOriginal(el) {
    if (el.dataset.originalValue) el.textContent = el.dataset.originalValue
  }

  monthsPerInterval() {
    return this.intervalValue === "year" ? 12 : 1
  }

  couponCoversRenewal(data) {
    if (data.coupon_duration === "forever") return true
    if (data.coupon_duration === "repeating") return Number(data.coupon_duration_in_months) > this.monthsPerInterval()
    return false
  }

  // Renewal = plan price for the interval, discounted only while the coupon still applies, plus VAT.
  // The row itself is hidden whenever the coupon still covers the renewal (forever, or a repeating
  // coupon that outlasts the interval) because then the renewal equals the first payment already shown.
  updateRenewal(data) {
    const covers = this.couponCoversRenewal(data)
    const netCents = covers
      ? Math.max(this.renewalBaseCentsValue - data.plan_discount_cents, 0)
      : this.renewalBaseCentsValue
    const renewalText = this.formatCents(Math.round(netCents * (1 + this.vatRateValue / 100)))
    if (this.hasSummaryRenewalTarget) this.rememberAndWrite(this.summaryRenewalTarget, renewalText)
    // The "activate now" card quotes the renewal in its copy; it follows the same number
    // even while the summary row is hidden because the coupon still covers the renewal.
    for (const el of this.renewalEchoTargets) this.rememberAndWrite(el, renewalText)
    if (this.hasSummaryRenewalRowTarget) {
      const row = this.summaryRenewalRowTarget
      if (row.dataset.originalHidden === undefined) {
        row.dataset.originalHidden = row.classList.contains("d-none") ? "true" : "false"
      }
      row.classList.toggle("d-none", data.coupon_duration === "forever" || covers)
    }
  }

  restoreRenewal() {
    if (this.hasSummaryRenewalTarget) this.restoreOriginal(this.summaryRenewalTarget)
    for (const el of this.renewalEchoTargets) this.restoreOriginal(el)
    if (this.hasSummaryRenewalRowTarget && this.summaryRenewalRowTarget.dataset.originalHidden !== undefined) {
      const wasHidden = this.summaryRenewalRowTarget.dataset.originalHidden === "true"
      this.summaryRenewalRowTarget.classList.toggle("d-none", wasHidden)
    }
  }

  // Same id as the hidden promotion_code fields, so the server re-validates it
  // on the switch and renders the other mode discounted from the first paint.
  writeModeLinks(promotionCodeId) {
    for (const link of this.modeLinkTargets) {
      const url = new URL(link.href)
      if (promotionCodeId) {
        url.searchParams.set("promotion_code", promotionCodeId)
      } else {
        url.searchParams.delete("promotion_code")
      }
      link.href = url.toString()
    }
  }

  writeCouponSentence(data) {
    if (!this.hasCouponSentenceTarget) return
    const templates = {
      once: this.#texts.sentenceOnce,
      repeating: this.repeatingTemplate(data.coupon_duration_in_months),
      forever: this.#texts.sentenceForever
    }
    const template = templates[data.coupon_duration] || ""
    this.couponSentenceTarget.textContent = template
      .replace("%{name}", data.coupon_name)
      .replace("%{count}", String(data.coupon_duration_in_months ?? ""))
  }

  repeatingTemplate(count) {
    return pluralText(this.sentenceRepeatingTextsValue, document.documentElement.lang, Number(count))
  }

  formatCents(cents) {
    return formatBillingAmount(cents / 100, this.currencyFormatValue)
  }

  escapeHtml(text) {
    const div = document.createElement("div")
    div.textContent = text
    return div.innerHTML
  }
}
